/**
 * PostHog Event Definitions
 *
 * Central registry for all analytics events tracked in the application.
 * Each event includes its name constant and TypeScript type for properties.
 */

import { FundingPolicyType } from '@shared';

// ============================================================================
// EVENT NAMES
// ============================================================================

export const POSTHOG_CUSTOM_EVENTS = {
  // User Authentication & Onboarding
  USER_SIGNED_IN: 'user_signed_in',
  ADVISOR_SIGNED_UP: 'advisor_signed_up',
  ADVISOR_ONBOARDING_STEP_COMPLETED: 'advisor_onboarding_step_completed',
  ADVISOR_ONBOARDING_COMPLETED: 'advisor_onboarding_completed',

  // D2C Funnel
  D2C_ACCESS_CLICKED: 'd2c_access_clicked',
  D2C_VIRTUAL_MEETING_CLICKED: 'd2c_virtual_meeting_clicked',
  D2C_CHECKOUT_STARTED: 'd2c_checkout_started',
  D2C_PAYMENT_COMPLETED: 'd2c_payment_completed',

  // Advisor Subscription
  ADVISOR_CHECKOUT_VIEWED: 'advisor_checkout_viewed',

  // Guided Planning Flow
  GUIDED_PLANNING_STARTED: 'guided_planning_started',
  GUIDED_PLANNING_STEP_VIEWED: 'guided_planning_step_viewed',
  GUIDED_PLANNING_COMPLETED: 'guided_planning_completed',

  // Care Planning & Assumptions
  CARE_247_TOGGLE_CHANGED: 'care_247_toggle_changed',
  CARE_ASSUMPTIONS_EDIT: 'care_assumptions_edit',

  // CCRC (Continuing Care Retirement Community)
  CCRC_TYPE_A_SELECTION_VIEWED: 'ccrc_type_a_selection_viewed',
  CCRC_TYPE_C_FEASIBILITY_VIEWED: 'ccrc_type_c_feasibility_viewed',
  CCRC_CONTRACT_SELECTED: 'ccrc_contract_selected',

  // Quotes & Shopping
  QUOTES_VIEWED: 'quotes_viewed',
  SHOP_INSURANCE_QUOTE_ADDED_TO_PLAN: 'shop_insurance_quote_added_to_plan',
  SHOP_INSURANCE_ILLUSTRATION_GENERATED: 'shop_insurance_illustration_generated',

  // Policy Application
  POLICY_APPLICATION_STARTED: 'policy_application_started',
  POLICY_APPLICATION_FORM_STARTED: 'policy_application_form_started',
  POLICY_APPLICATION_AI_PREFILL: 'policy_application_ai_prefill',
  APPLICATION_SUBMITTED: 'application_submitted',

  // Reports & Downloads
  PDF_REPORT_DOWNLOADED: 'pdf_report_downloaded',
  PDF_REPORT_EMAILED: 'pdf_report_emailed',

  // Underwriting
  UNDERWRITING_VIEWED: 'underwriting_viewed',
  CHECK_ELIGIBILITY_CLICKED: 'check_eligibility_clicked',

  // Anonymous PUI
  PUI_CTA_CLICKED: 'pui_cta_clicked',
  PUI_SUBMITTED: 'pui_submitted',
} as const;

// ============================================================================
// EVENT PROPERTY TYPES
// ============================================================================

// Advisor Subscription
export type AdvisorCheckoutViewedProperties = {
  advisorId: string;
  planType: string;
};

// Guided Planning
export type GuidedPlanningStartedProperties = {
  clientId: string;
  variant: string;
  stepCount: number;
  hasPartner: boolean;
};

// Care Planning
export type Care247ToggleProperties = {
  clientId: string;
  enabled: boolean;
  carePhase: 'fullCare';
  careEnvironment: 'home';
  previousHours?: number;
};

export type CareAssumptionsEditProperties = {
  clientId: string;
  changedFields?: string[];
  hasUnsavedChanges?: boolean;
  ltcAtAge?: number;
  healthcareInflation?: number;
  phaseDurations?: Record<string, number>;
};

// CCRC Events
export type CcrcSelectionProperties = {
  clientId: string;
  ccrcType: 'type-a' | 'type-c';
  stepId: string;
};

export type CcrcProductSelectedProperties = {
  clientId: string;
  ccrcType: 'type-a' | 'type-c';
  productId?: string;
  productName?: string;
  monthlyFee?: number;
  entryFee?: number;
};

// Quotes
export type QuotesViewedProperties = {
  clientId: string;
  quoteCount: number;
  noQuotesReason?: GuidedPlanningNoQuotesReason;
};

export type ShopInsuranceQuoteAddedToPlanProperties = {
  clientId: string;
  quoteId: string;
  quoteType: 'life-insurance' | 'traditional-ltc' | 'short-term-care';
};

export type ShopInsuranceIllustrationGeneratedProperties = {
  clientId: string;
  quoteId: string;
};

// Policy Application
export type PolicyApplicationStartedProperties = {
  clientId: string;
  policyApplicationId: string;
  policyType: FundingPolicyType;
  currentStep: string;
};

export type PolicyApplicationFormStartedProperties = {
  clientId: string;
  policyApplicationId: string;
  formType: string;
  formId: string;
};

export type ApplicationSubmittedProperties = {
  clientId: string;
  policyApplicationId: string;
  fundingPolicyType?: FundingPolicyType;
  carrier: string;
  formName: string;
  formId: string;
};

export type PolicyApplicationAiPrefillProperties = {
  clientId: string;
  policyApplicationId: string;
  accepted: boolean;
};

// Underwriting
export type UnderwritingViewedProperties = {
  clientId: string;
  productName: string;
  fundingPolicyType?: FundingPolicyType;
};

export type CheckEligibilityClickedProperties = {
  clientId: string;
  fundingPolicyType?: FundingPolicyType;
};

export type PuiSubmittedProperties = {
  clientId: string;
  products: string[];
};

// PDF Reports
export type PdfReportDownloadedProperties = {
  clientId: string;
  includeCareJourney: boolean;
  includePredictedCareNeeds: boolean;
};

// ============================================================================
// UNION TYPE FOR ALL EVENT PROPERTIES
// ============================================================================

export type PostHogCustomEventProperties =
  | AdvisorCheckoutViewedProperties
  | GuidedPlanningStartedProperties
  | Care247ToggleProperties
  | CareAssumptionsEditProperties
  | CcrcSelectionProperties
  | CcrcProductSelectedProperties
  | QuotesViewedProperties
  | ShopInsuranceQuoteAddedToPlanProperties
  | ShopInsuranceIllustrationGeneratedProperties
  | PolicyApplicationStartedProperties
  | PolicyApplicationFormStartedProperties
  | PolicyApplicationAiPrefillProperties
  | ApplicationSubmittedProperties
  | UnderwritingViewedProperties
  | CheckEligibilityClickedProperties
  | PuiSubmittedProperties
  | PdfReportDownloadedProperties;
